import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { fetchBookingSlot } from '../../../api/TurfBookingsAPI';
import { getUsers } from '../../../api/UsersAPI';
import { createContributionsForSlot, fetchContributionsForSlot, updateContributionsForSlot } from '../../../api/ContributionsAPI';
import { handleApiError } from '../../../api/risapi';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';

const ContributionsForm = ({ formType }) => {
    const { slotId } = useParams();
    const navigate = useNavigate();
    const isEditMode = formType === 'edit';

    const [slot, setSlot] = useState(null);
    const [members, setMembers] = useState([]);
    const [playerSelections, setPlayerSelections] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [selectedPlayers, setSelectedPlayers] = useState([]); // For multi-select

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true); // Ensure loading state is true at the start
            try {
                const [slotRes, usersRes] = await Promise.all([
                    new Promise(r => fetchBookingSlot(slotId, r)),
                    new Promise(r => getUsers({ paginate: false, status: 'active' }, r))
                ]);

                if (slotRes.booking_slot) {
                    setSlot(slotRes.booking_slot);
                } else {
                    Swal.fire('Error', 'Could not load booking slot details or unauthorized.', 'error')
                        .then(() => navigate('/club_portal/game-finances')); // Navigate back to the tabbed page
                    return;
                }

                const clubMembers = usersRes.users?.data || usersRes.users || [];
                debugger;
                setMembers(clubMembers);

                let initialSelections = {};
                let initialSelectedPlayers = [];

                if (isEditMode) {
                    const contributionsRes = await new Promise(r => fetchContributionsForSlot(slotId, r));
                    clubMembers.forEach(member => {
                        const contribution = contributionsRes.contributions?.find(c => c.user_id === member.id);
                        const memberPlayed = contribution ? !!contribution.member_played : false;
                        initialSelections[member.id] = {
                            user_id: member.id,
                            member_played: memberPlayed,
                            guest_count: contribution ? contribution.guest_count : 0,
                            guest_names: contribution ? (contribution.guests || []).map(g => g.guest_name) : [],
                            payable_by: contribution ? contribution.payable_by : null,
                            discount: member.active_discount || 0,
                        };
                        // Populate multi-select if member played
                        if (memberPlayed) {
                            initialSelectedPlayers.push({ value: member.id, label: member.name });
                        }
                    });
                } else {
                    // Initialize for create mode
                    clubMembers.forEach(member => {
                        initialSelections[member.id] = {
                            user_id: member.id, member_played: false, guest_count: 0,
                            guest_names: [], payable_by: null, discount: member.active_discount || 0,
                        };
                    });
                }
                setPlayerSelections(initialSelections);
                setSelectedPlayers(initialSelectedPlayers); // Set initial multi-select state
            } catch (error) {
                console.error("Error loading data:", error);
                Swal.fire('Error', 'Failed to load necessary data.', 'error');
                navigate('/club_portal/game-finances'); // Navigate back on error
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [slotId, isEditMode, navigate]);

    // Handler for multi-select dropdown changes
    const handleMultiSelectChange = (selectedOptions) => {
        const selectedIds = selectedOptions.map(option => option.value);
        setSelectedPlayers(selectedOptions);

        setPlayerSelections(prev => {
            const newState = { ...prev };
            Object.keys(newState).forEach(userId => {
                newState[userId].member_played = selectedIds.includes(parseInt(userId));
            });
            return newState;
        });
    };

    // Handler for individual checkbox changes
    const handleMemberPlayedChange = (userId, isChecked) => {
        setPlayerSelections(prev => ({ ...prev, [userId]: { ...prev[userId], member_played: isChecked } }));

        const memberOption = { value: userId, label: members.find(m => m.id === userId)?.name };
        if (isChecked) {
            setSelectedPlayers(prev => [...prev, memberOption].filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i));
        } else {
            setSelectedPlayers(prev => prev.filter(option => option.value !== userId));
        }
    };

    // Handler for guest count changes
    const handleGuestCountChange = (userId, count) => {
        const guestCount = Math.max(0, parseInt(count) || 0);
        setPlayerSelections(prev => ({
            ...prev,
            [userId]: {
                ...prev[userId],
                guest_count: guestCount,
                // Ensure guest_names array matches the new count
                guest_names: Array.from({ length: guestCount }, (_, i) => prev[userId].guest_names[i] || '')
            }
        }));
    };

    // Handler for guest name changes
    const handleGuestNameChange = (userId, guestIndex, name) => {
        setPlayerSelections(prev => {
            const newSelections = { ...prev };
            newSelections[userId].guest_names[guestIndex] = name;
            return newSelections;
        });
    };

    // Handler for "Paid By" dropdown changes
    const handlePayableByChange = (userId, selectedOption) => {
        setPlayerSelections(prev => ({ ...prev, [userId]: { ...prev[userId], payable_by: selectedOption ? selectedOption.value : null } }));
    };

    // Logic to calculate final shares (memoized for performance)
    const { finalShares, totalHeads, sharePerHead } = useMemo(() => {
        const players = Object.values(playerSelections).filter(p => p.member_played || p.guest_count > 0);
        if (!slot || players.length === 0) return { finalShares: {}, totalHeads: 0, sharePerHead: '0.00' };

        const heads = players.reduce((acc, p) => acc + (p.member_played ? 1 : 0) + p.guest_count, 0);
        if (heads === 0) return { finalShares: {}, totalHeads: 0, sharePerHead: '0.00' };

        const baseShare = slot.cost / heads;
        let totalDiscountValue = 0;
        let totalPayingHeads = 0;

        players.forEach(p => {
            const headCount = (p.member_played ? 1 : 0) + p.guest_count;
            totalDiscountValue += (baseShare * headCount) * ((p.discount || 0) / 100);
            if ((p.discount || 0) < 100) {
                totalPayingHeads += headCount;
            }
        });

        const surcharge = totalPayingHeads > 0 ? totalDiscountValue / totalPayingHeads : 0;
        const shares = {};

        players.forEach(p => {
            const headCount = (p.member_played ? 1 : 0) + p.guest_count;
            const playerBaseCost = baseShare * headCount;
            const discountAmount = playerBaseCost * ((p.discount || 0) / 100);
            let finalAmount = playerBaseCost - discountAmount;
            if ((p.discount || 0) < 100) {
                finalAmount += surcharge * headCount;
            }
            // Ensure final amount is not negative due to floating point inaccuracies
            shares[p.user_id] = Math.max(0, finalAmount).toFixed(2);
        });
        return { finalShares: shares, totalHeads: heads, sharePerHead: baseShare.toFixed(2) };
    }, [playerSelections, slot]); // Removed members dependency as discount comes from initial load

    // Handler for form submission
    const handleSubmit = () => {
        const playersData = Object.values(playerSelections).filter(p => p.member_played || p.guest_count > 0);
        if (playersData.length === 0) {
            Swal.fire('No Players Selected', 'Please select at least one player or add a guest.', 'warning');
            return;
        }
        setIsLoading(true);
        const apiCall = isEditMode ? updateContributionsForSlot : createContributionsForSlot;
        
        apiCall(slotId, playersData, (response) => {
            setIsLoading(false);
            if (response.contributions) {
                Swal.fire('Success!', `Contributions ${isEditMode ? 'updated' : 'generated'}!`, 'success')
                    .then(() => navigate('/club_portal/game-finances', { state: { message: `Contributions ${isEditMode ? 'updated' : 'generated'} successfully!` } })); // Navigate back to the tabbed page
            } else {
                handleApiError(response, setErrors, setToaster, setShowToaster);
            }
        });
    };

    // Prepare options for react-select dropdowns
    const allMemberOptions = members.map(member => ({ value: member.id, label: member.name }));

    if (isLoading) return <div className="text-center p-5"><div className="spinner-border"></div></div>;

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title={`${isEditMode ? 'Edit' : 'Generate'} Contributions`} />
                <div className="card">
                    <div className="card-header">
                        <div className="alert bg-primary text-white d-flex justify-content-around mb-0">
                            <span><strong>Total Cost:</strong> ₹{slot?.cost}</span>
                            <span><strong>Total Heads:</strong> {totalHeads}</span>
                            <span><strong>Base Share/Head:</strong> ₹{sharePerHead}</span>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="mb-4">
                            <label className="form-label fw-bold">Quick Select Players Who Played:</label>
                            <Select
                                isMulti
                                options={allMemberOptions}
                                value={selectedPlayers}
                                onChange={handleMultiSelectChange}
                                closeMenuOnSelect={false}
                                placeholder="Select multiple players..."
                                isDisabled={isLoading}
                            />
                        </div>
                        <hr/>
                        <h5>Individual Player Details & Guests</h5>
                        <div className="row mt-3">
                            {members.map(member => {
                                const selection = playerSelections[member.id];
                                // Ensure selection exists before rendering
                                if (!selection) return null;
                                const isSelected = selection.member_played || selection.guest_count > 0;
                                return (
                                <div key={member.id} className="col-md-4 col-lg-3 mb-3">
                                    <div className={`card h-100 ${isSelected ? 'border-primary shadow' : ''}`}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between">
                                                <div className="form-check form-switch mb-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`member-${member.id}`}
                                                        checked={selection.member_played}
                                                        onChange={(e) => handleMemberPlayedChange(member.id, e.target.checked)}
                                                        disabled={isLoading}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor={`member-${member.id}`}>{member.name}</label>
                                                </div>
                                                {selection.discount > 0 && <span className="badge bg-success">{selection.discount}% Off</span>}
                                            </div>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text">Guests</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="0"
                                                    value={selection.guest_count}
                                                    onChange={(e) => handleGuestCountChange(member.id, e.target.value)}
                                                    disabled={isLoading}
                                                />
                                            </div>
                                            {selection.guest_count > 0 && (
                                                <div className="mt-2 ps-3">{[...Array(selection.guest_count)].map((_, i) => (
                                                    <input
                                                        key={i}
                                                        type="text"
                                                        className="form-control form-control-sm mb-1"
                                                        placeholder={`Guest ${i + 1} Name`}
                                                        value={selection.guest_names[i] || ''}
                                                        onChange={(e) => handleGuestNameChange(member.id, i, e.target.value)}
                                                        disabled={isLoading}
                                                    />
                                                ))}</div>
                                            )}
                                            {isSelected && (
                                                <div className="mt-2">
                                                    <label className="form-label form-label-sm">Paid By</label>
                                                    <Select
                                                        isClearable
                                                        options={allMemberOptions}
                                                        value={allMemberOptions.find(opt => opt.value === selection.payable_by)}
                                                        onChange={(opt) => handlePayableByChange(member.id, opt)}
                                                        placeholder="Self"
                                                        isDisabled={isLoading}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <div className="card-footer bg-light"><strong>Final Share: ₹{finalShares[member.id] || '0.00'}</strong></div>
                                        )}
                                    </div>
                                </div>
                            )})}
                        </div>
                    </div>
                    <div className="card-footer text-end">
                        <button className="btn btn-secondary me-2" onClick={() => navigate('/club_portal/game-finances')} disabled={isLoading}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleSubmit} disabled={isLoading || totalHeads === 0}>
                            {isLoading ? 'Saving...' : (isEditMode ? 'Update Contributions' : 'Generate Contributions')}
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default ContributionsForm;